import { useState } from "react";
import { ListChecks, Check, Trash2, Plus, BellRing, BellOff } from "lucide-react";
import { TASK_PRIORITIES } from "./palette";
import { RemindPicker } from "./RemindPicker";

const NO_REMIND = { lead: "none", time: "09:00", at: "" };

export const TaskList = ({ tasks, overdue, accent, todayISO, onAdd, onPatch, onDelete }) => {
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("normal");
  const [remind, setRemind] = useState(NO_REMIND);
  const [showDone, setShowDone] = useState(false);
  const [openRemind, setOpenRemind] = useState("");

  const shown = tasks.filter((t) => showDone || !t.done);

  const add = () => {
    if (!text.trim()) return;
    onAdd({ text: text.trim(), due, priority, remind });
    setText("");
    setDue("");
    setPriority("normal");
    setRemind(NO_REMIND);
  };

  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-panel p-4" data-testid="task-list">
      <div className="flex flex-wrap items-center gap-3">
        <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest" style={{ color: accent }}>
          <ListChecks className="h-3.5 w-3.5" /> My list
        </p>
        {overdue.length > 0 && (
          <span className="rounded-full border border-orange-400/40 bg-orange-400/10 px-3 py-1 text-xs font-semibold text-orange-300"
                data-testid="task-overdue-count">
            {overdue.length} still waiting
          </span>
        )}
        <button
          data-testid="task-show-done"
          onClick={() => setShowDone((s) => !s)}
          className="ms-auto text-xs text-ink-soft underline decoration-dotted hover:text-ink"
        >
          {showDone ? "Hide what's done" : "Show what's done"}
        </button>
      </div>

      <div className="mt-3 space-y-1.5" data-testid="tasks">
        {shown.length === 0 && (
          <p className="text-xs text-ink-soft">Nothing on your list — add the first thing below.</p>
        )}
        {shown.map((t) => (
          <div key={t.task_id} className="flex flex-wrap items-center gap-2 text-sm">
            <button
              data-testid={`task-tick-${t.task_id}`}
              onClick={() => onPatch(t, { done: !t.done })}
              aria-label={t.done ? "Mark as not done" : "Mark as done"}
              className={`rounded-full border p-1 transition-colors ${
                t.done ? "border-forest bg-forest/15 text-forest" : "border-white/20 text-ink-soft hover:text-ink"
              }`}
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <span className="h-2 w-2 rounded-full" title={TASK_PRIORITIES[t.priority]?.label}
                  style={{ backgroundColor: TASK_PRIORITIES[t.priority]?.tint }} />
            <span dir="auto" className={t.done ? "text-ink-soft line-through" : ""}>{t.text}</span>
            {t.due && (
              <span
                className={`text-xs tabular-nums ${
                  !t.done && t.due < todayISO ? "font-semibold text-orange-300" : "text-ink-soft"
                }`}
              >
                {!t.done && t.due < todayISO ? `overdue · ${t.due}` : t.due}
              </span>
            )}
            {t.due && (
              <button
                data-testid={`task-remind-toggle-${t.task_id}`}
                onClick={() => setOpenRemind((id) => (id === t.task_id ? "" : t.task_id))}
                title={t.remind_label || "Remind me before this"}
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors"
                style={t.remind_at
                  ? { borderColor: accent, color: accent, backgroundColor: `${accent}18` }
                  : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
              >
                {t.remind_at ? <BellRing className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
                {t.remind_at
                  ? (t.remind_sent ? "Reminded" : t.remind_label || "Reminder set")
                  : "Remind me"}
              </button>
            )}
            <button
              data-testid={`task-delete-${t.task_id}`}
              onClick={() => onDelete(t)}
              aria-label="Delete task"
              className="ms-auto rounded-full border border-white/10 p-1 text-ink-soft hover:border-destructive/60 hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            {openRemind === t.task_id && (
              <div className="w-full ps-8" data-testid={`task-remind-row-${t.task_id}`}>
                <RemindPicker
                  value={{ lead: t.remind_lead || "none", time: t.remind_time || "09:00",
                           at: t.remind_custom || "" }}
                  onChange={(spec) => onPatch(t, { remind: spec })}
                  accent={accent}
                  testid={`task-remind-${t.task_id}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <input
          data-testid="task-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          maxLength={200}
          placeholder="e.g. book the visa appointment"
          className="min-h-[40px] flex-1 rounded-xl border border-white/10 bg-page px-4 text-sm outline-none placeholder:text-ink-soft"
        />
        <input
          data-testid="task-due"
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className="min-h-[40px] rounded-xl border border-white/10 bg-page px-3 text-xs outline-none"
        />
        <select
          data-testid="task-priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="min-h-[40px] rounded-xl border border-white/10 bg-page px-3 text-xs outline-none"
        >
          {Object.entries(TASK_PRIORITIES).map(([key, p]) => (
            <option key={key} value={key}>{p.label}</option>
          ))}
        </select>
        {due && <RemindPicker value={remind} onChange={setRemind} accent={accent} testid="task-new-remind" />}
        <button
          data-testid="task-add"
          onClick={add}
          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-5 text-sm font-semibold text-page active:scale-95"
          style={{ backgroundColor: accent }}
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
    </div>
  );
};
